import React, { useMemo } from "react";
import { useQuery } from "react-query";
import { Card, Typography, Spin, Empty, Row, Col, Progress, Button } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { getOverdueCollections } from "../../../services/dashboard.service";
import { formatCurrency } from "../../../services/utils";

const { Title, Text } = Typography;

export default function OverdueCollections() {
  const { data, isLoading } = useQuery(
    "getOverdueCollections",
    getOverdueCollections,
    {
      refetchOnWindowFocus: false,
    }
  );

  const reportData = data?.data || [];

  // Process data to calculate totals and sort ranges
  const { totalOverdue, sortedData, recentOverdue } = useMemo(() => {
    if (!Array.isArray(reportData)) return { totalOverdue: 0, sortedData: [], recentOverdue: 0 };

    const total = reportData.reduce((acc: number, curr: any) => acc + parseFloat(curr.total), 0);
    
    // Find value for <= 30 days (assuming this represents "recent")
    const recent = reportData.find((item: any) => item.overdue_range === "<= 30")?.total || 0;

    // Sort order map
    const sortOrder: { [key: string]: number } = {
      "<= 30": 1,
      "31-60": 2,
      "61-90": 3,
      "> 90": 4
    };

    const sorted = [...reportData].sort((a: any, b: any) => {
      return (sortOrder[a.overdue_range] || 99) - (sortOrder[b.overdue_range] || 99);
    });

    return { totalOverdue: total, sortedData: sorted, recentOverdue: recent };
  }, [reportData]);

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Cobranças vencidas
            </Title>
          </div>
        </div>
      }
      bordered={false}
      style={{ height: "100%" }}
    >
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spin />
        </div>
      ) : sortedData.length > 0 ? (
        <div>
          <div style={{ marginBottom: 20 }}>
            <Title level={2} style={{ margin: 0 }}>
              {formatCurrency(totalOverdue)}
            </Title>
            <Text type="secondary">
              {formatCurrency(parseFloat(recentOverdue))} vencidas em até 30 dias
            </Text>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {sortedData.map((item: any, index: number) => (
              <Row key={index} gutter={16} align="middle">
                <Col span={6}>
                  <Text strong>{item.overdue_range} dias</Text>
                </Col>
                <Col span={18}>
                  <Progress
                    percent={(parseFloat(item.total) / (totalOverdue || 1)) * 100}
                    showInfo={false}
                    strokeColor={
                      item.overdue_range === "<= 30"
                        ? "#ffccc7" // Lighter red for recent
                        : item.overdue_range === "31-60"
                        ? "#ff7875"
                        : "#ff4d4f" // Darker red for older
                    }
                    trailColor="#f0f0f0"
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatCurrency(parseFloat(item.total))}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        ({item.count})
                    </Text>
                  </div>
                </Col>
              </Row>
            ))}
          </div>
        </div>
      ) : (
        <Empty description="Sem cobranças vencidas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Card>
  );
}
